import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardBody, Button, Chip } from '@heroui/react';
import { ExternalLink, BookOpen, CheckCircle, Package } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Product } from '@/services/apis/products';
import { useTranslation } from '@/hooks/useTranslation';

interface ProductCardProps {
  product: Product;
  onShowDetails: (product: Product) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, onShowDetails }) => {
  const { t } = useTranslation('products');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'operational':
        return { color: 'success' as const, variant: 'flat' as const };
      case 'beta':
        return { color: 'warning' as const, variant: 'flat' as const };
      case 'maintenance':
        return { color: 'danger' as const, variant: 'flat' as const };
      default:
        return { color: 'default' as const, variant: 'flat' as const };
    }
  };

  const getCategoryColor = (categoryName: string) => {
    const colors = ['primary', 'secondary', 'success', 'warning', 'danger'] as const;
    const hash = categoryName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  const handleOpenTool = () => {
    if (product.url) {
      window.open(product.url, '_blank', 'noopener,noreferrer');
    } else {
      onShowDetails(product);
    }
  };

  const statusConfig = getStatusColor(product.status);
  const categoryColor = getCategoryColor(product.category?.name || 'default');

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      whileHover={{ scale: 1.02 }}
      className="h-full"
    >
      <Card className="h-full bg-white dark:bg-gray-900 border border-gray-200/60 dark:border-gray-800/60 shadow-sm hover:shadow-xl transition-all duration-300 group">
        {/* Header with gradient background */}
        <div className="relative overflow-hidden bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-white/20 via-transparent to-transparent" />
          <CardBody className="relative p-6 text-white">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1 min-w-0 pr-2">
                <div className="flex items-center gap-2 mb-2">
                  {product.icon ? <span className="text-2xl">{product.icon}</span> : <Package className="w-6 h-6" />}
                  <h3 className="text-xl font-bold line-clamp-1">{product.name}</h3>
                </div>
                {product.tagline && (
                  <p className="text-white/85 text-sm line-clamp-2 leading-relaxed">{product.tagline}</p>
                )}
              </div>
              <Chip
                size="sm"
                color={statusConfig.color}
                variant={statusConfig.variant}
                className="backdrop-blur-md bg-white/25 border border-white/30 text-white font-semibold shadow-lg flex-shrink-0"
              >
                {t(`status.${product.status}`)}
              </Chip>
            </div>

            {/* Category */}
            {product.category && (
              <Chip
                size="sm"
                color={categoryColor}
                variant="flat"
                className="backdrop-blur-md bg-white/25 border border-white/30 text-white font-semibold shadow-lg"
              >
                {product.category.name}
              </Chip>
            )}
          </CardBody>
        </div>

        {/* Content */}
        <CardBody className="p-6 flex-1 flex flex-col bg-gradient-to-b from-white to-gray-50/50 dark:from-gray-900 dark:to-gray-950/50">
          {/* Metrics */}
          {product.metrics && Object.keys(product.metrics).length > 0 && (
            <div className="grid grid-cols-3 gap-4 mb-6">
              {Object.entries(product.metrics)
                .slice(0, 3)
                .map(([key, value]) => (
                  <div key={key} className="text-center">
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">{String(value)}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </div>
                  </div>
                ))}
            </div>
          )}

          {/* Features */}
          {product.features && product.features.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">{t('card.keyFeatures')}</h4>
              <div className="space-y-2">
                {product.features.slice(0, 3).map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span className="text-sm text-gray-600 dark:text-gray-400 line-clamp-1">{feature}</span>
                  </div>
                ))}
                {product.features.length > 3 && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 ml-6">
                    +{product.features.length - 3} {t('card.more')}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Target Users */}
          {product.targetUsers && product.targetUsers.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{t('card.userRoles')}</h4>
              <div className="flex flex-wrap gap-1">
                {product.targetUsers.slice(0, 3).map((user, index) => (
                  <Chip key={index} size="sm" variant="flat" className="text-xs">
                    {user}
                  </Chip>
                ))}
                {product.targetUsers.length > 3 && (
                  <Chip size="sm" variant="flat" className="text-xs">
                    +{product.targetUsers.length - 3} more
                  </Chip>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-2 mt-auto">
            <Button
              color="primary"
              variant="solid"
              className="w-full font-semibold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-300"
              startContent={<ExternalLink className="w-4 h-4" />}
              onPress={handleOpenTool}
            >
              {product.url ? t('card.openTool') : t('card.comingSoon')}
            </Button>
            <div className="flex space-x-2">
              <Button variant="bordered" className="flex-1 text-sm" onPress={() => onShowDetails(product)}>
                {t('card.moreInfo')}
              </Button>
              <Button
                as={Link}
                variant="bordered"
                className="flex-1 text-sm"
                startContent={<BookOpen className="w-4 h-4" />}
                to={`/guides?productId=${product.id}`}
              >
                {t('card.guides')}
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>
    </motion.div>
  );
};

export default ProductCard;
